import React, { useState, useCallback, useEffect } from 'react';
import { timeDelta } from '../utils';
import api from '../../services/api';
import CountdownTimer from '../common/CountdownTimer';
import { TeamLogo } from '../common/TeamLogo';

function TransferBlock({transfers, isLoading, error, playerWyId, leagueId}) {
  const resolvedLeagueId = leagueId || null;
  const bidContextKey = playerWyId && resolvedLeagueId ? `${resolvedLeagueId}:${playerWyId}` : null;
  const [bidState, setBidState] = useState({
    key: null,
    activeBids: [],
    loading: false,
    error: null
  });
  const isBidStateCurrent = bidState.key === bidContextKey;
  const activeBids = isBidStateCurrent ? bidState.activeBids : [];
  const bidLoading = Boolean(bidContextKey) && (!isBidStateCurrent || bidState.loading);
  const bidError = isBidStateCurrent ? bidState.error : null;

  // Fetch active bids for this player
  const fetchActiveBids = useCallback(async () => {
    console.log('TransferBlock: Fetching active bids for player:', playerWyId, 'leagueId:', resolvedLeagueId);

    const response = await api.get(`/transfers/bids/player/${playerWyId}`, {
      params: { leagueId: resolvedLeagueId }
    });
    console.log('TransferBlock: API response:', response);
    console.log('TransferBlock: Response success:', response.success);
    console.log('TransferBlock: Response data (bids array):', response.data);

    const bidsData = response.data || [];
    console.log('TransferBlock: Setting active bids:', bidsData);

    if (bidsData.length > 0) {
      console.log('TransferBlock: Found', bidsData.length, 'active bids');
    } else {
      console.log('TransferBlock: No active bids found for player', playerWyId);
    }

    return bidsData;
  }, [playerWyId, resolvedLeagueId]);

  useEffect(() => {
    let cancelled = false;

    if (!playerWyId || !resolvedLeagueId) {
      if (!playerWyId) {
        console.log('TransferBlock: No playerWyId provided');
      }
      if (!resolvedLeagueId) {
        console.log('TransferBlock: No leagueId available; skipping fetch until league is resolved');
      }
      setBidState({
        key: null,
        activeBids: [],
        loading: false,
        error: null
      });
      return undefined;
    }

    // Only fetch once we have both playerWyId and a resolved leagueId
    const loadInitialBids = async () => {
      setBidState({
        key: bidContextKey,
        activeBids: [],
        loading: true,
        error: null
      });

      try {
        const bidsData = await fetchActiveBids();
        if (!cancelled) {
          setBidState({
            key: bidContextKey,
            activeBids: bidsData,
            loading: false,
            error: null
          });
        }
      } catch (error) {
        console.error('TransferBlock: Error fetching active bids:', error);
        console.error('TransferBlock: Error response:', error.response);
        if (!cancelled) {
          setBidState({
            key: bidContextKey,
            activeBids: [],
            loading: false,
            error: 'Failed to load active bids: ' + (error.response?.data?.error || error.message)
          });
        }
      }
    };

    loadInitialBids();

    return () => {
      cancelled = true;
    };
  }, [playerWyId, resolvedLeagueId, bidContextKey, fetchActiveBids]); // Re-run when league becomes available

  useEffect(() => {
    // Set up periodic refresh only if we have an active bid
    if (!bidContextKey || activeBids.length === 0) {
      return undefined;
    }

    let cancelled = false;
    const interval = setInterval(async () => {
      try {
        const bidsData = await fetchActiveBids();
        if (!cancelled) {
          setBidState({
            key: bidContextKey,
            activeBids: bidsData,
            loading: false,
            error: null
          });
        }
      } catch (error) {
        console.error('TransferBlock: Error refreshing active bids:', error);
        if (!cancelled) {
          setBidState((currentState) => {
            if (currentState.key !== bidContextKey) {
              return currentState;
            }

            return {
              ...currentState,
              loading: false,
              error: 'Failed to load active bids: ' + (error.response?.data?.error || error.message)
            };
          });
        }
      }
    }, 30000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [bidContextKey, activeBids.length, fetchActiveBids]);

  // Active bids display
  const renderActiveBids = () => {
    if (bidLoading) {
      return (
        <li className="teams-list-row">
          <div className="col"></div>
          <div className="col title">Loading active bids...</div>
        </li>
      );
    }

    if (bidError) {
      return (
        <li className="teams-list-row">
          <div className="col"></div>
          <div className="col title" style={{ color: 'red' }}>
            {bidError}
          </div>
        </li>
      );
    }

    if (!activeBids || activeBids.length === 0) {
      return (
        <li className="teams-list-row">
          <div className="col">No active bids</div>
        </li>
      );
    }

    return activeBids.map((bid) => {
      const hasImage = bid.team?.logo;
      return (
        <li key={bid._id} className="teams-list-row">
          <div className={`col title ${hasImage ? 'has-image' : ''}`}>
            {hasImage && (
              <div className="image">
                <TeamLogo logoUrl={bid.team.logo} teamName={bid.team.name} />
              </div>
            )}
            <span>{bid.team?.name || 'Unknown Team'}</span>
          </div>
          <div className="col">
            €{(bid.amount / 1000000).toFixed(1)}M (Active Bid)
          </div>
          <div className="col">
            <CountdownTimer 
              expiresAt={bid.expiresAt}
              className="bid-timer"
            />
          </div>
        </li>
      );
    });
  };

  // Transfer history display
  const transferItems = transfers?.map((item, index) => {
    const hasImage = item.team?.logo;
    return (
      <li key={index} className="teams-list-row">
        <div className={`col title ${hasImage ? 'has-image' : ''}`}>
          {hasImage && (
            <div className="image">
              <TeamLogo logoUrl={item.team.logo} teamName={item.team.name} />
            </div>
          )}
          <span>{item.team?.name || 'Unknown Team'}</span>
        </div>
        <div className="col">
          €{item.fee ? (item.fee / 1000000).toFixed(1) + 'M' : 'Free'}
        </div>
        <div className="col">
          {new Date(item.date).toLocaleDateString()}
        </div>
      </li>
    );
  }) || [];

  // Calculate if any bids are active for header timer
  const getEarliestExpiration = () => {
    if (!activeBids || activeBids.length === 0) return null;
    
    return activeBids.reduce((earliest, bid) => {
      const bidExpiry = new Date(bid.expiresAt).getTime();
      return bidExpiry < earliest ? bidExpiry : earliest;
    }, new Date(activeBids[0].expiresAt).getTime());
  };

  const earliestExpiration = getEarliestExpiration();

  return (
    <div className="stats-block">
      <div className="title-block">
        <h4>Transfer Activity</h4>
        
        {earliestExpiration && (
          <div className="timer-display">
            Bidding ends in: <CountdownTimer 
              expiresAt={new Date(earliestExpiration)}
              className="header-timer"
            />
          </div>
        )}
      </div>

      <div className="transfers-block">
        <div className="teams-list-row header-row">
          <div className="info-col">Team name</div>
          <div className="info-col">Transfer offer</div>
          <div className="info-col">Time remaining</div>
        </div>

        {/* Show active bids first */}
        {renderActiveBids()}
        
        {/* Then show transfer history */}
        {transferItems}
      </div>
    </div>
  );
}

export {TransferBlock};
